import React from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  UploadCloud,
  Trash2,
  FileText,
  FileSpreadsheet,
  FileImage,
  FileCode,
  FileVideo,
  FileAudio,
  FileArchive,
  FileJson,
  Folder,
  FolderPlus,
  ChevronRight,
  ChevronDown,
  Search,
  X,
  Info,
  Edit2,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Document, Folder as FolderType, FolderCreateRequest } from '../types';
import { formatDate } from '../utils/dateUtils';
import { truncateDisplayName } from '../utils/textUtils';
import { getRateLimitFromError, dispatchRateLimitExceeded, isAuthError } from '../utils/rateLimit';
import { apiService } from '../services/api';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import ConnectorLogo, { hasConnectorLogo } from '../components/ConnectorLogo';
import RenameFolderModal from '../components/folders/RenameFolderModal';
import DeleteFolderModal from '../components/folders/DeleteFolderModal';

/** Get icon and styles for a document based on filename extension */
function getFileTypeIcon(filename: string): {
  Icon: React.ComponentType<{ className?: string }>;
  bgClass: string;
  iconClass: string;
} {
  const ext = (filename || '').split('.').pop()?.toLowerCase() || '';
  const text = ['pdf', 'doc', 'docx', 'rtf', 'odt', 'txt', 'md', 'odf'];
  const spreadsheet = ['xls', 'xlsx', 'ods', 'csv'];
  const image = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'];
  const code = ['py', 'js', 'ts', 'tsx', 'jsx', 'html', 'css', 'xml', 'yaml', 'yml', 'sh', 'sql'];
  const video = ['mp4', 'webm', 'mov', 'avi', 'mkv'];
  const audio = ['mp3', 'wav', 'ogg', 'm4a', 'flac'];
  const archive = ['zip', 'tar', 'gz', 'rar', '7z'];

  if (ext === 'pdf') return { Icon: FileText, bgClass: 'bg-red-100', iconClass: 'text-red-600' };
  if (ext === 'json') return { Icon: FileJson, bgClass: 'bg-amber-100', iconClass: 'text-amber-600' };
  if (text.includes(ext)) return { Icon: FileText, bgClass: 'bg-blue-100', iconClass: 'text-blue-600' };
  if (spreadsheet.includes(ext)) return { Icon: FileSpreadsheet, bgClass: 'bg-green-100', iconClass: 'text-green-600' };
  if (image.includes(ext)) return { Icon: FileImage, bgClass: 'bg-purple-100', iconClass: 'text-purple-600' };
  if (code.includes(ext)) return { Icon: FileCode, bgClass: 'bg-gray-100', iconClass: 'text-gray-600' };
  if (video.includes(ext)) return { Icon: FileVideo, bgClass: 'bg-pink-100', iconClass: 'text-pink-600' };
  if (audio.includes(ext)) return { Icon: FileAudio, bgClass: 'bg-indigo-100', iconClass: 'text-indigo-600' };
  if (archive.includes(ext)) return { Icon: FileArchive, bgClass: 'bg-yellow-100', iconClass: 'text-yellow-600' };
  return { Icon: FileText, bgClass: 'bg-amber-100', iconClass: 'text-amber-600' };
}

const KnowledgeBase: React.FC = () => {
  const [documents, setDocuments] = React.useState<Document[]>([]);
  const [kbFolders, setKbFolders] = React.useState<FolderType[]>([]);
  const [flatFolders, setFlatFolders] = React.useState<FolderType[]>([]);
  const [selectedFolderId, setSelectedFolderId] = React.useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = React.useState<Set<string>>(new Set());
  const [loading, setLoading] = React.useState(true);
  const [foldersLoading, setFoldersLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [deleteConfirmId, setDeleteConfirmId] = React.useState<string | null>(null);
  const [deletingDocId, setDeletingDocId] = React.useState<string | null>(null);
  const [folderToDelete, setFolderToDelete] = React.useState<FolderType | null>(null);
  const [createSubfolderParentId, setCreateSubfolderParentId] = React.useState<string | undefined>(undefined);
  const [showCreateFolderModal, setShowCreateFolderModal] = React.useState(false);
  const [showRenameFolderModal, setShowRenameFolderModal] = React.useState(false);
  const [renameFolderId, setRenameFolderId] = React.useState<string | undefined>(undefined);
  const [renameFolderName, setRenameFolderName] = React.useState<string | undefined>(undefined);
  const [renameFolderDescription, setRenameFolderDescription] = React.useState<string | undefined>(undefined);
  const [newFolderName, setNewFolderName] = React.useState('');
  const [newFolderParentId, setNewFolderParentId] = React.useState<string | undefined>(undefined);
  const [creatingFolder, setCreatingFolder] = React.useState(false);
  const [uploadFolderId, setUploadFolderId] = React.useState<string | undefined>(undefined);
  const [showUploadFolderSelector, setShowUploadFolderSelector] = React.useState(false);
  const [showCreateFolderFirstModal, setShowCreateFolderFirstModal] = React.useState(false);
  const [showInfoModal, setShowInfoModal] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [folderIdToConnectorType, setFolderIdToConnectorType] = React.useState<Record<string, string>>({});

  const displayDocuments = React.useMemo(() => {
    if (!searchQuery.trim()) return documents;
    const q = searchQuery.toLowerCase();
    return documents.filter(
      (doc) =>
        doc.title?.toLowerCase().includes(q) ||
        doc.filename?.toLowerCase().includes(q) ||
        doc.summary_text?.toLowerCase().includes(q)
    );
  }, [documents, searchQuery]);

  const getFolderPath = React.useCallback((folderId: string): string => {
    const findPath = (folders: FolderType[], targetId: string, path: string[] = []): string[] | null => {
      for (const folder of folders) {
        if (folder.id === targetId) return [...path, folder.name];
        if (folder.children?.length) {
          const found = findPath(folder.children, targetId, [...path, folder.name]);
          if (found) return found;
        }
      }
      return null;
    };
    const path = findPath(kbFolders, folderId);
    return path ? path.join(' / ') : '';
  }, [kbFolders]);

  const fetchFolders = React.useCallback(async () => {
    setFoldersLoading(true);
    try {
      const [tree, flat] = await Promise.all([
        apiService.getFolders(false, 'knowledge_base'),
        apiService.getFolders(true, 'knowledge_base'),
      ]);
      setKbFolders(tree);
      setFlatFolders(flat);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load knowledge bases');
      setKbFolders([]);
      setFlatFolders([]);
    } finally {
      setFoldersLoading(false);
    }
  }, []);

  const fetchDocuments = React.useCallback(async () => {
    setLoading(true);
    try {
      if (selectedFolderId) {
        const docs = await apiService.getDocuments(undefined, selectedFolderId);
        setDocuments(docs);
      } else {
        const docs = await apiService.getDocuments(undefined, undefined, { folderType: 'knowledge_base' });
        setDocuments(docs);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documents');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [selectedFolderId]);

  React.useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  React.useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  React.useEffect(() => {
    apiService.getDataSources().then((res) => {
      const map: Record<string, string> = {};
      (res.data_sources || []).forEach((ds: { folder_id?: string; connector_type?: string }) => {
        if (ds.folder_id && ds.connector_type) map[ds.folder_id] = ds.connector_type;
      });
      setFolderIdToConnectorType(map);
    }).catch(() => setFolderIdToConnectorType({}));
  }, [kbFolders.length]);

  const consumeUploadStream = async (stream: ReadableStream<Uint8Array>, fallbackName: string) => {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalResult: { doc_id?: string; title?: string; status?: string; chunks?: number } | null = null;
    let uploadError: Error | null = null;
    let fileSaved = false;
    let queued = false;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split('\n\n');
      buffer = events.pop() || '';

      for (const event of events) {
        if (!event.trim() || !event.startsWith('data: ')) continue;
        const jsonStr = event.slice(6).trim();
        if (!jsonStr || jsonStr === '[DONE]') continue;
        try {
          const parsed = JSON.parse(jsonStr);
          const eventType = parsed.event;
          const data = parsed.data || {};
          if (eventType === 'status') {
            const phase = data.phase;
            if (phase === 'file_saved') {
              fileSaved = true;
              finalResult = { doc_id: data.doc_id, title: data.title || fallbackName, status: 'processing', chunks: 0 };
            } else if (phase === 'queuing') {
              queued = true;
              if (finalResult) finalResult.status = 'queued';
            }
            if (fileSaved && queued && data.doc_id) {
              reader.releaseLock();
              return finalResult || { doc_id: data.doc_id, title: fallbackName, status: 'processing', chunks: 0 };
            }
          } else if (eventType === 'completed') {
            finalResult = data;
            reader.releaseLock();
            return finalResult;
          } else if (eventType === 'error') {
            uploadError = new Error(data.detail || `Failed to upload "${fallbackName}".`);
            reader.releaseLock();
            throw uploadError;
          }
        } catch {
          // ignore parse errors
        }
      }
    }
    reader.releaseLock();
    if (uploadError) throw uploadError;
    return finalResult;
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length) return;
    const folderIdToUse = uploadFolderId || selectedFolderId || undefined;
    try {
      setUploading(true);
      const uploadPromises = Array.from(files).map(async (file) => {
        try {
          const stream = await apiService.uploadDocumentStream(file, undefined, folderIdToUse);
          return consumeUploadStream(stream, file.name);
        } catch (err) {
          if (isAuthError(err)) throw err; // Bubble to outer catch so we return without showing upload toast
          const { isRateLimit, message: limitMsg } = getRateLimitFromError(err);
          if (isRateLimit) {
            dispatchRateLimitExceeded(limitMsg);
            throw err;
          }
          toast.error(`Failed to upload "${file.name}": ${err instanceof Error ? err.message : 'Unknown error'}`);
          throw err;
        }
      });
      await Promise.all(uploadPromises);
      await fetchDocuments();
      await fetchFolders();
      setUploadFolderId(undefined);
    } catch (err) {
      if (isAuthError(err)) return; // Interceptor shows session expired and redirects
      const { isRateLimit } = getRateLimitFromError(err);
      if (!isRateLimit) {
        toast.error(err instanceof Error ? err.message : 'Failed to upload');
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (docId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (deleteConfirmId !== docId) {
      setDeleteConfirmId(docId);
      return;
    }
    setDeletingDocId(docId);
    try {
      await apiService.deleteDocument(docId);
      toast.success('Document deleted');
      await fetchDocuments();
      await fetchFolders();
      setDeleteConfirmId(null);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
        : err instanceof Error ? err.message : 'Failed to delete';
      toast.error(String(msg));
    } finally {
      setDeletingDocId(null);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Folder name is required');
      return;
    }
    setCreatingFolder(true);
    try {
      const payload: FolderCreateRequest = {
        name: newFolderName.trim(),
        parent_id: newFolderParentId || createSubfolderParentId,
        folder_type: 'knowledge_base',
      };
      await apiService.createFolder(payload);
      toast.success('Knowledge base folder created');
      setShowCreateFolderModal(false);
      setNewFolderName('');
      setNewFolderParentId(undefined);
      setCreateSubfolderParentId(undefined);
      await fetchFolders();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to create folder');
    } finally {
      setCreatingFolder(false);
    }
  };

  const toggleFolderExpansion = (folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  const handleUploadToFolder = (folderId: string) => {
    setUploadFolderId(folderId);
    fileInputRef.current?.click();
  };

  const formatDateLocal = (dateString: string) =>
    formatDate(dateString, { year: 'numeric', month: 'short', day: 'numeric' });

  const renderFolderTree = (folders: FolderType[], level: number = 0): React.ReactNode => {
    return folders.map((folder) => {
      const hasChildren = folder.children && folder.children.length > 0;
      const isExpanded = expandedFolders.has(folder.id);
      const isSelected = selectedFolderId === folder.id;
      const isDataSource = !!folder.data_source_id;

      return (
        <div key={folder.id} className="group">
          <div
            className={`flex items-center py-1.5 px-2 rounded hover:bg-gray-100 cursor-pointer ${
              isSelected ? 'bg-amber-50 text-amber-800' : ''
            }`}
            style={{ paddingLeft: `${level * 1.5 + 0.5}rem` }}
            onClick={() => setSelectedFolderId(folder.id)}
          >
            {hasChildren ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFolderExpansion(folder.id);
                }}
                className="mr-0.5 p-0.5 hover:bg-gray-200 rounded"
              >
                {isExpanded ? <ChevronDown className="h-3 w-3 text-gray-500" /> : <ChevronRight className="h-3 w-3 text-gray-500" />}
              </button>
            ) : (
              <span className="w-4" />
            )}
            <BookOpen className="h-3.5 w-3.5 mr-1.5 text-amber-600 flex-shrink-0" />
            {isDataSource && hasConnectorLogo(folderIdToConnectorType[folder.id]) && (
              <ConnectorLogo connectorType={folderIdToConnectorType[folder.id]} size={12} className="mr-1 flex-shrink-0" alt="" />
            )}
            <span className="flex-1 text-sm truncate min-w-0" title={folder.name || undefined}>{folder.name}</span>
            {folder.document_count !== undefined && folder.document_count > 0 && (
              <span className="text-xs text-gray-500 ml-1.5 flex-shrink-0">({folder.document_count})</span>
            )}
            <div className="flex items-center ml-1.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUploadToFolder(folder.id);
                }}
                className="p-0.5 text-amber-600 hover:bg-amber-50 rounded"
                title="Upload to this folder"
              >
                <UploadCloud className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setCreateSubfolderParentId(folder.id);
                  setNewFolderParentId(folder.id);
                  setShowCreateFolderModal(true);
                }}
                className="p-0.5 text-amber-600 hover:bg-amber-50 rounded"
                title="Create subfolder"
              >
                <FolderPlus className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setRenameFolderId(folder.id);
                  setRenameFolderName(folder.name);
                  setRenameFolderDescription(folder.description);
                  setShowRenameFolderModal(true);
                }}
                className="p-0.5 text-amber-600 hover:bg-amber-50 rounded"
                title="Rename folder"
              >
                <Edit2 className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setFolderToDelete(folder);
                }}
                className="p-0.5 text-red-600 hover:bg-red-50 rounded"
                title="Delete folder"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          </div>
          {hasChildren && isExpanded && (
            <div>{renderFolderTree(folder.children!, level + 1)}</div>
          )}
        </div>
      );
    });
  };

  if (error && documents.length === 0 && !loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
        <p className="text-red-600 mb-4">{error}</p>
        <Link to="/" className="text-amber-600 hover:underline">Go back</Link>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-screen bg-gray-50 w-full min-w-0 overflow-hidden">
      <div className="border-b border-gray-200 px-6 py-4 bg-white flex-shrink-0">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-amber-600" />
            Knowledge Base
            <button
              type="button"
              onClick={() => setShowInfoModal(true)}
              className="ml-2 p-1 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-full transition-colors"
              title="Learn about Knowledge Base"
            >
              <Info className="h-5 w-5" />
            </button>
          </h1>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setCreateSubfolderParentId(undefined);
                setNewFolderParentId(undefined);
                setShowCreateFolderModal(true);
              }}
            >
              <FolderPlus className="h-4 w-4 mr-2" />
              New Folder
            </Button>
            <Button
              onClick={() => {
                if (kbFolders.length === 0) {
                  setShowCreateFolderFirstModal(true);
                  return;
                }
                if (selectedFolderId) {
                  fileInputRef.current?.click();
                } else {
                  setUploadFolderId(flatFolders[0]?.id ?? undefined);
                  setShowUploadFolderSelector(true);
                }
              }}
              disabled={uploading}
            >
              <UploadCloud className="h-4 w-4 mr-2" />
              {uploading ? 'Uploading...' : selectedFolderId ? 'Upload to Folder' : 'Upload Document'}
            </Button>
          </div>
        </div>
        <p className="text-gray-600 mt-2">
          Add documents in a folder tree. Knowledge bases are not in your chat sidebar—add them as context to any folder to use when chatting.
        </p>
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          multiple
          onChange={handleFileUpload}
        />
      </div>

      <div className="flex-1 flex overflow-hidden min-h-0 min-w-0">
        <div className="w-64 flex-shrink-0 border-r border-gray-200 bg-white flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900">Knowledge Base Folders</h2>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {foldersLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600" />
              </div>
            ) : (
              <>
                <div
                  className={`group flex items-center py-2 px-2 rounded hover:bg-gray-100 cursor-pointer mb-1 ${
                    selectedFolderId === null ? 'bg-amber-50 text-amber-800' : ''
                  }`}
                  onClick={() => setSelectedFolderId(null)}
                >
                  <BookOpen className="h-4 w-4 mr-2 text-amber-600 flex-shrink-0" />
                  <span className="flex-1 text-sm font-medium">All Documents</span>
                  <span className="text-xs text-gray-500">({documents.length})</span>
                  <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (kbFolders.length === 0) {
                          setShowCreateFolderFirstModal(true);
                          return;
                        }
                        setUploadFolderId(flatFolders[0]?.id);
                        setShowUploadFolderSelector(true);
                      }}
                      className="p-1 text-amber-600 hover:bg-amber-50 rounded"
                      title="Upload document"
                    >
                      <UploadCloud className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCreateSubfolderParentId(undefined);
                        setNewFolderParentId(undefined);
                        setShowCreateFolderModal(true);
                      }}
                      className="p-1 text-amber-600 hover:bg-amber-50 rounded"
                      title="Create root folder"
                    >
                      <FolderPlus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {kbFolders.length > 0 ? renderFolderTree(kbFolders) : <p className="text-sm text-gray-500 py-4 text-center">No folders yet</p>}
              </>
            )}
          </div>
        </div>

        <div className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto px-6 py-6 bg-gray-50">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
            </div>
          ) : (
            <>
              <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-gray-200">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by title, filename, or content..."
                    className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>
              </div>

              {displayDocuments.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center py-12">
                  <BookOpen className="h-16 w-16 text-amber-200 mb-4" />
                  <p className="text-gray-600 text-lg font-medium">
                    {searchQuery.trim() ? 'No documents match your search' : selectedFolderId ? 'No documents in this folder' : 'No documents yet'}
                  </p>
                  <p className="text-gray-500 mt-2">
                    {searchQuery.trim()
                      ? 'Try a different search or clear the search.'
                      : selectedFolderId
                        ? 'Upload documents to this folder or select another folder.'
                        : kbFolders.length === 0
                          ? 'Create a folder first, then you can upload documents.'
                          : 'Upload documents or create a folder to get started.'}
                  </p>
                </div>
              ) : (
              <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden bg-white">
                {displayDocuments.map((doc) => {
                  const status = doc.status || doc.processing_status || '';
                  const isReady = status === 'ready' || status === 'completed';
                  const isProcessing = ['processing', 'queued', 'pending', 'indexing', 'uploaded'].includes(status);
                  const isFailed = status === 'failed' || status === 'error';
                  const { Icon, bgClass, iconClass } = getFileTypeIcon(doc.filename || '');
                  return (
                    <div
                      key={doc.id}
                      className="py-2.5 px-3 flex items-center gap-3 hover:bg-gray-50 transition-colors relative group"
                    >
                      <div
                        className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                        onClick={() => window.open(`/document/${doc.doc_id}`, '_blank')}
                      >
                        <div className={`w-10 h-10 flex-shrink-0 ${bgClass} rounded-lg flex items-center justify-center`}>
                          <Icon className={`h-5 w-5 ${iconClass}`} />
                        </div>
                        <div className="flex-1 min-w-0 max-w-[100ch]">
                          <p className="text-sm font-medium text-gray-900 truncate" title={doc.title}>
                            {truncateDisplayName(doc.title)}
                            <span className="ml-2 text-gray-500 font-normal">· {doc.chunks ?? 0} chunks</span>
                            <span className="ml-2 text-gray-500 font-normal">· {formatDateLocal(doc.created_at)}</span>
                            <span className="ml-2">
                              <span
                                className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                                  isReady ? 'bg-green-100 text-green-800' : isProcessing ? 'bg-yellow-100 text-yellow-800' : isFailed ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {isReady ? 'Ready' : isProcessing ? 'Processing' : isFailed ? 'Failed' : status || '—'}
                              </span>
                            </span>
                          </p>
                          {doc.summary_text && (
                            <p className="text-xs text-gray-500 truncate mt-0.5" title={doc.summary_text}>
                              {(doc.summary_text || '').length > 75 ? `${(doc.summary_text || '').slice(0, 75)}…` : doc.summary_text}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        {deleteConfirmId === doc.doc_id ? (
                          <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-2 py-1">
                            <span className="text-xs text-red-700 font-medium">
                              {deletingDocId === doc.doc_id ? 'Deleting…' : 'Delete?'}
                            </span>
                            {deletingDocId !== doc.doc_id && (
                              <>
                                <button type="button" onClick={(e) => handleDelete(doc.doc_id, e)} className="px-2 py-0.5 bg-red-600 text-white text-xs rounded hover:bg-red-700">Yes</button>
                                <button type="button" onClick={() => setDeleteConfirmId(null)} className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300">No</button>
                              </>
                            )}
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => handleDelete(doc.doc_id, e)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Delete document"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Upload folder selector modal - folder required for Knowledge Base */}
      {showUploadFolderSelector && (
        <Modal isOpen onClose={() => setShowUploadFolderSelector(false)} title="Select folder for upload">
          <p className="text-sm text-gray-600 mb-3">Documents must be uploaded to a folder. Select a folder below.</p>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {flatFolders.map((folder) => (
              <label key={folder.id} className={`flex items-center px-3 py-2 rounded cursor-pointer hover:bg-gray-50 ${uploadFolderId === folder.id ? 'bg-amber-50' : ''}`}>
                <input type="radio" name="uploadFolder" checked={uploadFolderId === folder.id} onChange={() => setUploadFolderId(folder.id)} className="mr-3 h-4 w-4 text-amber-600" />
                <Folder className="h-4 w-4 mr-2 text-gray-500" />
                <span className="text-sm text-gray-900">{getFolderPath(folder.id) || folder.name}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowUploadFolderSelector(false)}>Cancel</Button>
            <Button onClick={() => { setShowUploadFolderSelector(false); fileInputRef.current?.click(); }} disabled={!uploadFolderId}>Upload</Button>
          </div>
        </Modal>
      )}

      {/* Create folder first (required for upload) */}
      <Modal
        isOpen={showCreateFolderFirstModal}
        onClose={() => setShowCreateFolderFirstModal(false)}
        title="Create a folder first"
        maxWidth="sm"
      >
        <div className="text-center pt-1 pb-2">
          <div className="mx-auto w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-4">
            <FolderPlus className="h-7 w-7 text-amber-600" />
          </div>
          <p className="text-gray-600 text-sm leading-relaxed mb-6">
            Documents in the Knowledge Base need to live in a folder. Create a folder, then you can upload your documents there.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button
              variant="outline"
              onClick={() => setShowCreateFolderFirstModal(false)}
            >
              Close
            </Button>
            <Button
              onClick={() => {
                setShowCreateFolderFirstModal(false);
                setCreateSubfolderParentId(undefined);
                setNewFolderParentId(undefined);
                setShowCreateFolderModal(true);
              }}
            >
              <FolderPlus className="h-4 w-4 mr-2" />
              Create folder
            </Button>
          </div>
        </div>
      </Modal>

      {/* Create folder modal */}
      <Modal
        isOpen={showCreateFolderModal}
        onClose={() => {
          setShowCreateFolderModal(false);
          setNewFolderName('');
          setNewFolderParentId(undefined);
          setCreateSubfolderParentId(undefined);
        }}
        title={createSubfolderParentId ? 'Create subfolder' : 'New knowledge base folder'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Folder name</label>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="e.g. Company policies"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
            />
          </div>
          {flatFolders.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Parent folder (optional)</label>
              <select
                value={newFolderParentId || createSubfolderParentId || ''}
                onChange={(e) => {
                  setNewFolderParentId(e.target.value || undefined);
                  setCreateSubfolderParentId(e.target.value || undefined);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                disabled={!!createSubfolderParentId}
              >
                <option value="">None (root)</option>
                {flatFolders.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowCreateFolderModal(false)}>Cancel</Button>
            <Button onClick={handleCreateFolder} disabled={creatingFolder || !newFolderName.trim()}>
              {creatingFolder ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </div>
      </Modal>

      <RenameFolderModal
        isOpen={showRenameFolderModal}
        onClose={() => {
          setShowRenameFolderModal(false);
          setRenameFolderId(undefined);
          setRenameFolderName(undefined);
          setRenameFolderDescription(undefined);
        }}
        folderId={renameFolderId}
        currentName={renameFolderName}
        currentDescription={renameFolderDescription}
        onSuccess={fetchFolders}
      />

      {/* Delete folder confirmation - same modal as Chat / Global Docs */}
      <DeleteFolderModal
        isOpen={!!folderToDelete}
        onClose={() => setFolderToDelete(null)}
        folderId={folderToDelete?.id}
        folderName={folderToDelete?.name}
        hasSubfolders={!!(folderToDelete?.children?.length)}
        documentCount={folderToDelete?.document_count ?? 0}
        threadCount={0}
        onDeleteConfirm={async (id, _force) => {
          try {
            await apiService.deleteFolder(id, true);
            toast.success('Folder deleted');
            setFolderToDelete(null);
            await fetchFolders();
            if (selectedFolderId === id) setSelectedFolderId(null);
          } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to delete folder');
            throw err;
          }
        }}
      />

      {/* Knowledge Base Info Modal */}
      {showInfoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[85vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <BookOpen className="h-6 w-6 text-amber-600" />
                  </div>
                  <h2 className="text-2xl font-semibold text-gray-900">What is Knowledge Base?</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setShowInfoModal(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="space-y-6">
                {/* Definition */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <Info className="h-5 w-5 mr-2 text-amber-600" />
                    Definition
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Knowledge Base</strong> is a folder-based document library for your workspace. You upload and organize documents in a folder tree here. Unlike Global Documents, Knowledge Base documents are <strong>not</strong> in your chat sidebar by default—you add them as <strong>context to specific folders</strong> so they are used when chatting in those folders.
                  </p>
                </div>

                {/* How It Works */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <Folder className="h-5 w-5 mr-2 text-amber-600" />
                    How It Works
                  </h3>
                  <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-3">
                    <p className="text-gray-700 leading-relaxed">
                      Create folders, upload documents, and organize your content in a tree. Then, in your chat sidebar, you <strong>attach Knowledge Base folders as context</strong> to the folders you use for chatting. When you chat in a folder that has Knowledge Base context attached, those documents are included in the conversation.
                    </p>
                  </div>
                  <ul className="list-disc list-inside space-y-2 text-gray-700 ml-2">
                    <li>Organize documents in a folder tree (e.g. by project, team, or topic)</li>
                    <li>Add Knowledge Base folders as context to chat folders</li>
                    <li>Documents are used only in folders where you have attached that context</li>
                    <li>Different chat folders can use different Knowledge Base folders</li>
                  </ul>
                </div>

                {/* Knowledge Base vs Global Documents */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <BookOpen className="h-5 w-5 mr-2 text-amber-600" />
                    Knowledge Base vs Global Documents
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900 mb-1">Knowledge Base</p>
                      <p className="text-sm text-gray-700">
                        Folder-scoped. You choose which Knowledge Base folders to attach to which chat folders. Good for project-specific or topic-specific docs that only certain threads need.
                      </p>
                    </div>
                    <div className="pt-2 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-900 mb-1">Global Documents</p>
                      <p className="text-sm text-gray-700">
                        Available in every thread and folder. Use for company-wide policies, shared references, and anything that should always be in context.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Key Benefits */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Key Benefits</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <h4 className="font-semibold text-amber-900 mb-2">📁 Organized by topic</h4>
                      <p className="text-sm text-amber-800">
                        Use folders and subfolders to structure documents by project, team, or subject.
                      </p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900 mb-2">🎯 Scoped context</h4>
                      <p className="text-sm text-blue-800">
                        Attach only the relevant Knowledge Base folders to each chat folder—no clutter.
                      </p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="font-semibold text-green-900 mb-2">💬 Better answers</h4>
                      <p className="text-sm text-green-800">
                        Chat has access to the right documents for that folder, so answers stay on topic.
                      </p>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <h4 className="font-semibold text-purple-900 mb-2">🔄 Reuse across threads</h4>
                      <p className="text-sm text-purple-800">
                        Once attached to a folder, the same Knowledge Base context applies to all threads in that folder.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Best Use Cases */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Best Use Cases</h3>
                  <div className="space-y-2">
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-amber-600 text-xs font-semibold">1</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Project or product documentation</p>
                        <p className="text-sm text-gray-600">Docs for a specific project, attached only to that project&apos;s folder</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-amber-600 text-xs font-semibold">2</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Team or department wikis</p>
                        <p className="text-sm text-gray-600">Internal knowledge that only certain chat folders need</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-amber-600 text-xs font-semibold">3</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Topic-specific reference</p>
                        <p className="text-sm text-gray-600">Technical docs, FAQs, or guides scoped to relevant folders</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-amber-600 text-xs font-semibold">4</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Client or account materials</p>
                        <p className="text-sm text-gray-600">Documents for specific accounts, attached only where needed</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Important Note */}
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                  <div className="flex items-start">
                    <Info className="h-5 w-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-yellow-900 mb-1">Tip</p>
                      <p className="text-sm text-yellow-800">
                        To use Knowledge Base documents in chat, add them as <strong>context</strong> to your chat folders (e.g. from folder settings or when managing a folder). For documents that should be available in <strong>every</strong> thread, use Global Documents instead.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end">
              <Button onClick={() => setShowInfoModal(false)}>
                Got it, thanks!
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBase;
